import { GATEWAY_CONFIG } from '../config/config';
import { getAnonymousId } from './cacheService';
import logger from './logger';

/**
 * Service dédié à l'intégration VTO via la Gateway
 * La gateway orchestre l'authentification, la récupération des images et l'appel au service VTO
 */

/**
 * Convertit le genre du format frontend vers le format gateway
 * @param {string} gender - 'homme' ou 'femme'
 * @returns {string} - 'H' ou 'F'
 */
const toGatewayGender = (gender) => {
  return gender === 'homme' ? 'H' : 'F';
};

/**
 * Génère le VTO pour un utilisateur anonyme (sans compte) via la gateway
 * La gateway récupère l'image produit via product_id et appelle le service VTO avec le selfie
 * @param {Object} params
 * @param {string} params.selfieBase64 - Selfie en base64 (data URI ou raw)
 * @param {string} params.customerDomain - Domaine du marchand Shopify
 * @param {string} params.productId - ID du produit dans le Mirror Service
 * @param {string} params.gender - Genre ('homme' ou 'femme')
 * @param {number} params.height - Taille en cm
 * @param {number} params.weight - Poids en kg
 * @param {string} params.sizeTop - Taille haut ('S', 'M', 'L', etc.)
 * @param {string} params.sizeBottom - Taille bas ('S', 'M', 'L', etc.)
 * @returns {Promise<Object>} - { imageBase64 }
 */
export const generateVTOAnonymous = async ({
  selfieBase64,
  customerDomain,
  productId,
  gender,
  height,
  weight,
  computedMeasurements,
  sessionId,
}) => {
  try {
    if (!selfieBase64) throw new Error('Selfie requis');
    if (!customerDomain || !productId) throw new Error('customerDomain et productId requis');
    if (!computedMeasurements) throw new Error('computedMeasurements requis — appeler computeProfile() avant');

    logger.log('📤 Envoi requête VTO anonymous via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.VTO_ANONYMOUS}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain:   customerDomain,
          legacy_product_id: productId,
          user_identifier:   getAnonymousId(),
          selfie_base64:     selfieBase64,
          gender:            toGatewayGender(gender),
          height:            parseInt(height),
          weight:            parseInt(weight),
          chest_min:         computedMeasurements.chest_min,
          chest_max:         computedMeasurements.chest_max,
          waist_min:         computedMeasurements.waist_min,
          waist_max:         computedMeasurements.waist_max,
          hips_min:          computedMeasurements.hips_min,
          hips_max:          computedMeasurements.hips_max,
          session_id:        sessionId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.details) throw new Error(errorData.details);
      if (response.status === 400) throw new Error('PRODUCT_NOT_READY');
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    logger.log('✅ VTO anonymous généré via gateway');

    return { imageBase64: data.image_base64 };
  } catch (error) {
    logger.error('❌ Erreur VTO anonymous (gateway):', error);
    throw error;
  }
};

/**
 * Génère le VTO pour un utilisateur anonyme ayant déjà un avatar (2ème visite+)
 * La gateway reçoit directement l'avatar en base64 et appelle /vto/with-avatar
 * @param {Object} params
 * @param {string} params.avatarBase64 - Avatar issu du 1er VTO, en base64 (data URI ou raw)
 * @param {string} params.customerDomain - Domaine du marchand Shopify
 * @param {string} params.productId - ID du produit dans le Mirror Service
 * @param {string} params.sessionId - Session UUID
 * @returns {Promise<Object>} - { imageBase64 }
 */
export const generateVTOAnonymousWithAvatar = async ({
  avatarBase64,
  customerDomain,
  productId,
  sessionId,
}) => {
  try {
    if (!avatarBase64) throw new Error('Avatar requis');
    if (!customerDomain || !productId) throw new Error('customerDomain et productId requis');

    logger.log('📤 Envoi requête VTO anonymous-avatar via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.VTO_ANONYMOUS_AVATAR}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain:   customerDomain,
          legacy_product_id: productId,
          user_identifier:   getAnonymousId(),
          avatar_base64:     avatarBase64,
          session_id:        sessionId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.details) throw new Error(errorData.details);
      if (response.status === 400) throw new Error('PRODUCT_NOT_READY');
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    logger.log('✅ VTO anonymous-avatar généré via gateway');

    return { imageBase64: data.image_base64 };
  } catch (error) {
    logger.error('❌ Erreur VTO anonymous-avatar (gateway):', error);
    throw error;
  }
};

/**
 * Génère le VTO pour un shopper connecté via la gateway
 * La gateway récupère l'avatar et l'image produit depuis les services backend
 * @param {Object} params
 * @param {string} params.customerDomain - Domaine du marchand Shopify
 * @param {string} params.productId - ID du produit
 * @param {string} params.userIdentifier - ID BDD du shopper dans l'API Mobile
 * @param {string} params.firebaseToken - Token Firebase ID
 * @returns {Promise<Object>} - { imageBase64 }
 */
export const generateVTOShopper = async ({
  customerDomain,
  productId,
  userIdentifier,
  firebaseToken,
  sessionId,
}) => {
  try {
    if (!userIdentifier || !firebaseToken) {
      throw new Error('userIdentifier et firebaseToken requis');
    }
    if (!customerDomain || !productId) {
      throw new Error('customerDomain et productId requis');
    }

    logger.log('📤 Envoi requête VTO shopper via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.VTO_SHOPPER}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain: customerDomain,
          legacy_product_id: productId,
          user_identifier: userIdentifier,
          firebase_token: firebaseToken,
          session_id: sessionId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.details) throw new Error(errorData.details);
      if (response.status === 400) throw new Error('PRODUCT_NOT_READY');
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    logger.log('✅ VTO shopper généré via gateway');

    return { imageBase64: data.image_base64 };
  } catch (error) {
    logger.error('❌ Erreur VTO shopper (gateway):', error);
    throw error;
  }
};
